import { useEffect, useState } from 'react';
import styles from './alertGroup.module.scss';
import { AlertMessage, AlertsSortOrder } from './models';
import CustomAlert from './CustomAlert';
import UsersFailedInvitationsAlert from './Categories/UsersFailedInvitationsAlert';
import AccountCustomAlert from './Categories/AccountCustomAlert';
import ProductQuantityAlertContainer from './Categories/ProductQuantityAlertContainer';

const DismissedAlertsKey = '___dismissedalerts___';

interface PropType {
  messages: AlertMessage[];
}

function getDismissedAlerts(): string[] {
  const dismissedAlerts = localStorage.getItem(DismissedAlertsKey);
  if (dismissedAlerts) {
    try {
      return JSON.parse(dismissedAlerts) as string[];
    } catch {}
  }
  return [];
}

function addDismissedAlert(id: string) {
  const dismissedAlerts = getDismissedAlerts();
  dismissedAlerts.push(id);
  localStorage.setItem(DismissedAlertsKey, JSON.stringify(dismissedAlerts));
}

export default function AlertGroup({ messages }: PropType) {
  const [dismissedAlerts, setDismissedAlerts] = useState(() =>
    getDismissedAlerts(),
  );
  const visibleAlerts =
    messages
      ?.filter((m) => !dismissedAlerts.includes(m.id))
      ?.sort((a, b) => AlertsSortOrder[a.type] - AlertsSortOrder[b.type]) ?? [];

  const handleAlertClose = (alert: AlertMessage) => {
    addDismissedAlert(alert.id);
    setDismissedAlerts(getDismissedAlerts());
  };

  const multipleLowProductQuantityMessages =
    messages.filter((x) => x.category === 'product_quantity').length > 1;

  const [showLowQuantityAlert, setShowLowQuantityAlert] = useState(
    () => multipleLowProductQuantityMessages,
  );

  useEffect(() => {
    setDismissedAlerts(getDismissedAlerts());
    setShowLowQuantityAlert(multipleLowProductQuantityMessages);
  }, [messages]);

  if (!visibleAlerts.length) return null;

  return (
    <div className="mb-3">
      <div className="row gy-2 mb-3">
        {visibleAlerts
          .filter((x) => x.category === 'custom')
          .map((message) => (
            <div className={`col-12 ${styles.alertContainer}`} key={message.id}>
              <AccountCustomAlert
                message={message}
                onAlertClose={handleAlertClose}
              />
            </div>
          ))}

        {visibleAlerts
          .filter((x) => x.category === 'users_failed_invitations')
          .map((message) => (
            <div className={`col-12 ${styles.alertContainer}`} key={message.id}>
              <UsersFailedInvitationsAlert
                message={message}
                onAlertClose={handleAlertClose}
              />
            </div>
          ))}

        <ProductQuantityAlertContainer
          messages={visibleAlerts.filter(
            (x) => x.category === 'product_quantity',
          )}
          onAlertClose={handleAlertClose}
          setShowLowQuantityAlert={setShowLowQuantityAlert}
          showLowQuantityAlert={showLowQuantityAlert}
        />

        {visibleAlerts
          .filter((x) => x.category === 'static')
          .map((message) => (
            <div className={`col-12 ${styles.alertContainer}`} key={message.id}>
              <CustomAlert
                message={message}
                onAlertClose={handleAlertClose}
                textContent={message.message}
              />
            </div>
          ))}
      </div>
    </div>
  );
}
